// Mock data for Activity Log

export const ACTIVITY_TYPES = {
  CREATED: 'created',
  EDITED: 'edited',
  AUTOSAVED: 'autosaved',
  LOCKED: 'locked',
  UNLOCKED: 'unlocked',
  SIGNED: 'signed',
  STATUS_CHANGED: 'status_changed',
  COMMENTED: 'commented',
  DOCUMENT_UPLOADED: 'document_uploaded',
  MODULE_COMPLETED: 'module_completed',
};

export const MOCK_ACTIVITIES = [
  {
    id: 'activity-001',
    action: ACTIVITY_TYPES.CREATED,
    userId: 'user-002',
    userName: 'Admin User',
    userRole: 'admin',
    timestamp: new Date('2024-11-01T10:00:00'),
    details: {
      target: 'report',
      targetId: 'rep-001',
      targetName: 'Relazione Agronomica Asseverata 2024',
    },
  },
  {
    id: 'activity-002',
    action: ACTIVITY_TYPES.EDITED,
    userId: 'user-001',
    userName: 'Dr. Marco Rossi',
    userRole: 'reviewer',
    timestamp: new Date('2024-11-02T14:30:00'),
    details: {
      target: 'module',
      targetId: 'm1',
      targetName: 'Production trends & benchmark',
      changes: ['Updated production chart', 'Added RICA benchmark data'],
    },
  },
  {
    id: 'activity-003',
    action: ACTIVITY_TYPES.STATUS_CHANGED,
    userId: 'user-001',
    userName: 'Dr. Marco Rossi',
    userRole: 'reviewer',
    timestamp: new Date('2024-11-03T09:15:00'),
    details: {
      target: 'module',
      targetId: 'm1',
      targetName: 'Production trends & benchmark',
      oldStatus: 'draft',
      newStatus: 'needs_review',
    },
  },
  {
    id: 'activity-004',
    action: ACTIVITY_TYPES.LOCKED,
    userId: 'user-002',
    userName: 'Admin User',
    userRole: 'admin',
    timestamp: new Date('2024-11-04T11:00:00'),
    details: {
      target: 'module',
      targetId: 'm1',
      targetName: 'Production trends & benchmark',
      reason: 'Approved for final report',
    },
  },
  {
    id: 'activity-005',
    action: ACTIVITY_TYPES.COMMENTED,
    userId: 'user-001',
    userName: 'Dr. Marco Rossi',
    userRole: 'reviewer',
    timestamp: new Date('2024-11-20T10:30:00'),
    details: {
      target: 'module',
      targetId: 'm1',
      targetName: 'Production trends & benchmark',
      commentId: 'comment-001',
    },
  },
  {
    id: 'activity-006',
    action: ACTIVITY_TYPES.DOCUMENT_UPLOADED,
    userId: 'user-002',
    userName: 'Admin User',
    userRole: 'admin',
    timestamp: new Date('2024-11-15T10:30:00'),
    details: {
      target: 'document',
      targetId: 'doc-001',
      targetName: 'Fascicolo_Aziendale_2024.pdf',
    },
  },
  {
    id: 'activity-007',
    action: ACTIVITY_TYPES.MODULE_COMPLETED,
    userId: 'user-001',
    userName: 'Dr. Marco Rossi',
    userRole: 'reviewer',
    timestamp: new Date('2024-11-25T16:00:00'),
    details: {
      target: 'module',
      targetId: 'm3',
      targetName: 'Water savings',
    },
  },
];

// Helper function to filter activities by action type
export function filterActivitiesByType(activities, actionType) {
  if (!actionType || actionType === 'all') {
    return activities;
  }
  return activities.filter(activity => activity.action === actionType);
}

// Helper function to format activity message
export function formatActivityMessage(activity, t) {
  const { action, userName, details } = activity;
  
  // Backward-compatible fallback (no i18n)
  if (typeof t !== 'function') {
    switch (action) {
      case ACTIVITY_TYPES.CREATED:
        return `${userName} created ${details.targetName || details.target}`;
      case ACTIVITY_TYPES.EDITED:
        return `${userName} edited ${details.targetName || details.target}`;
      case ACTIVITY_TYPES.AUTOSAVED:
        return `${userName} autosaved ${details.targetName || details.target}`;
      case ACTIVITY_TYPES.LOCKED:
        return `${userName} locked ${details.targetName || details.target}`;
      case ACTIVITY_TYPES.UNLOCKED:
        return `${userName} unlocked ${details.targetName || details.target}`;
      case ACTIVITY_TYPES.SIGNED:
        return `${userName} signed the report`;
      case ACTIVITY_TYPES.STATUS_CHANGED:
        return `${userName} changed status of ${details.targetName || details.target} from ${details.oldStatus} to ${details.newStatus}`;
      case ACTIVITY_TYPES.COMMENTED:
        return `${userName} commented on ${details.targetName || details.target}`;
      case ACTIVITY_TYPES.DOCUMENT_UPLOADED:
        return `${userName} uploaded ${details.targetName || details.target}`;
      case ACTIVITY_TYPES.MODULE_COMPLETED:
        return `${userName} completed ${details.targetName || details.target}`;
      default:
        return `${userName} performed ${action} on ${details.targetName || details.target}`;
    }
  }

  const safeDetails = typeof details === 'object' && details ? details : {};
  const userLabel = userName || activity.user || t('reports.workspace.fallback.unknown');
  const targetLabel = safeDetails.targetName || safeDetails.target || t('reports.workspace.fallback.item');

  switch (action) {
    case ACTIVITY_TYPES.CREATED:
      return t('reports.workspace.activityLog.message.created', { user: userLabel, target: targetLabel });
    case ACTIVITY_TYPES.EDITED:
      return t('reports.workspace.activityLog.message.edited', { user: userLabel, target: targetLabel });
    case ACTIVITY_TYPES.AUTOSAVED:
      return t('reports.workspace.activityLog.message.autosaved', { user: userLabel, target: targetLabel });
    case ACTIVITY_TYPES.LOCKED:
      return t('reports.workspace.activityLog.message.locked', { user: userLabel, target: targetLabel });
    case ACTIVITY_TYPES.UNLOCKED:
      return t('reports.workspace.activityLog.message.unlocked', { user: userLabel, target: targetLabel });
    case ACTIVITY_TYPES.SIGNED:
      return t('reports.workspace.activityLog.message.signed', { user: userLabel });
    case ACTIVITY_TYPES.STATUS_CHANGED:
      return t('reports.workspace.activityLog.message.statusChanged', {
        user: userLabel,
        target: targetLabel,
        from: t(`reports.status.${safeDetails.oldStatus}`, { defaultValue: safeDetails.oldStatus }),
        to: t(`reports.status.${safeDetails.newStatus}`, { defaultValue: safeDetails.newStatus }),
      });
    case ACTIVITY_TYPES.COMMENTED:
    case 'comment_added':
      return t('reports.workspace.activityLog.message.commented', { user: userLabel, target: targetLabel });
    case ACTIVITY_TYPES.DOCUMENT_UPLOADED:
      return t('reports.workspace.activityLog.message.documentUploaded', { user: userLabel, target: targetLabel });
    case ACTIVITY_TYPES.MODULE_COMPLETED:
      return t('reports.workspace.activityLog.message.moduleCompleted', { user: userLabel, target: targetLabel });
    default:
      return t('reports.workspace.activityLog.message.performed', { user: userLabel, action, target: targetLabel });
  }
}

// Helper function to format activity timestamp
export function formatActivityTime(date, t) {
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);

  // Backward-compatible fallback (no i18n)
  if (typeof t !== 'function') {
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    const days = Math.floor(seconds / 86400);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    return `${months}mo ago`;
  }

  if (seconds < 60) return t('reports.workspace.activityLog.time.secondsAgo', { count: seconds });
  if (seconds < 3600) return t('reports.workspace.activityLog.time.minutesAgo', { count: Math.floor(seconds / 60) });
  if (seconds < 86400) return t('reports.workspace.activityLog.time.hoursAgo', { count: Math.floor(seconds / 3600) });
  const days = Math.floor(seconds / 86400);
  if (days < 30) return t('reports.workspace.activityLog.time.daysAgo', { count: days });
  const months = Math.floor(days / 30);
  return t('reports.workspace.activityLog.time.monthsAgo', { count: months });
}


